import React, { useContext, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import { Trash2, ShoppingBag, ShoppingCart, Plus, Minus } from 'lucide-react';
import { AppContext } from '../App';
import { toast } from 'sonner';
import { trackRemoveFromCart, trackBeginCheckout } from '../utils/analytics';
import { productUrl } from '../utils/slug';
import StripeInlineCheckout, { isStripeInlineAvailable } from '../components/StripeInlineCheckout';

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

const translations = {
  en: {
    yourCart: 'Your Cart',
    emptyCart: 'Your cart is empty',
    emptyCartDesc: 'Looks like you haven\'t added any stamps to your cart yet.',
    browseCatalog: 'Browse Our Catalog',
    quantity: 'Quantity',
    remove: 'Remove',
    removeItem: 'Remove Item',
    subtotal: 'Subtotal',
    shipping: 'Shipping',
    shippingFee: 'Registered Mail',
    discount: 'Discount',
    total: 'Total',
    proceedCheckout: 'Proceed to Checkout',
    backShopping: 'Back to Shopping',
    shippingInfo: 'Shipping Information',
    fullName: 'Full Name',
    email: 'Email',
    address: 'Address',
    city: 'City',
    country: 'Country',
    zipCode: 'Zip Code',
    paymentMethod: 'Payment Method',
    creditCard: 'Credit Card',
    completeOrder: 'Complete Order',
    processing: 'Processing...',
    loginRequired: 'Please login to checkout',
    itemRemoved: 'Item removed from cart',
    errorRemoving: 'Error removing item',
    checkoutFailed: 'Checkout failed. Please try again.',
    freeShipping: 'Free Shipping',
    freeShippingOn: 'Free Shipping on orders',
    discountPercent: '10% Discount',
    couponCode: 'Coupon Code',
    couponPlaceholder: 'Enter coupon code',
    applyCoupon: 'Apply',
    removeCoupon: 'Remove',
    couponApplied: 'Coupon applied!',
    couponInvalid: 'Invalid coupon code',
    couponDiscount: 'Coupon',
    youMayAlsoLike: 'You May Also Like',
    addToCartShort: 'Add'
  },
  tr: {
    yourCart: 'Sepetiniz',
    emptyCart: 'Sepetiniz boş',
    emptyCartDesc: 'Henüz sepetinize pul eklememişsiniz gibi görünüyor.',
    browseCatalog: 'Kataloğumuza Göz Atın',
    quantity: 'Adet',
    remove: 'Kaldır',
    removeItem: 'Ürünü Kaldır',
    subtotal: 'Ara Toplam',
    shipping: 'Kargo',
    shippingFee: 'Taahhütlü Posta',
    discount: 'İndirim',
    total: 'Toplam',
    proceedCheckout: 'Ödemeye Geç',
    backShopping: 'Alışverişe Dön',
    shippingInfo: 'Kargo Bilgileri',
    fullName: 'Ad Soyad',
    email: 'E-posta',
    address: 'Adres',
    city: 'Şehir',
    country: 'Ülke',
    zipCode: 'Posta Kodu',
    paymentMethod: 'Ödeme Yöntemi',
    creditCard: 'Kredi Kartı',
    completeOrder: 'Siparişi Tamamla',
    processing: 'İşleniyor...',
    loginRequired: 'Ödeme yapmak için lütfen giriş yapın',
    itemRemoved: 'Ürün sepetten kaldırıldı',
    errorRemoving: 'Ürün kaldırılırken hata oluştu',
    checkoutFailed: 'Ödeme başarısız. Lütfen tekrar deneyin.',
    freeShipping: 'Ücretsiz Kargo',
    freeShippingOn: 'Ücretsiz Kargo',
    discountPercent: '%10 İndirim',
    couponCode: 'Kupon Kodu',
    couponPlaceholder: 'Kupon kodunu girin',
    applyCoupon: 'Uygula',
    removeCoupon: 'Kaldır',
    couponApplied: 'Kupon uygulandı!',
    couponInvalid: 'Geçersiz kupon kodu',
    couponDiscount: 'Kupon',
    youMayAlsoLike: 'Bunları da Beğenebilirsiniz',
    addToCartShort: 'Ekle'
  },
  el: {
    yourCart: 'Το Καλάθι σας',
    emptyCart: 'Το καλάθι σας είναι άδειο',
    emptyCartDesc: 'Φαίνεται ότι δεν έχετε προσθέσει ακόμα γραμματόσημα στο καλάθι σας.',
    browseCatalog: 'Περιηγηθείτε στον Κατάλογό μας',
    quantity: 'Ποσότητα',
    remove: 'Αφαίρεση',
    removeItem: 'Αφαίρεση Προϊόντος',
    subtotal: 'Υποσύνολο',
    shipping: 'Αποστολή',
    shippingFee: 'Συστημένη Αποστολή',
    discount: 'Έκπτωση',
    total: 'Σύνολο',
    proceedCheckout: 'Προχωρήστε στην Ολοκλήρωση',
    backShopping: 'Επιστροφή στα Ψώνια',
    shippingInfo: 'Πληροφορίες Αποστολής',
    fullName: 'Πλήρες Όνομα',
    email: 'Email',
    address: 'Διεύθυνση',
    city: 'Πόλη',
    country: 'Χώρα',
    zipCode: 'Ταχυδρομικός Κώδικας',
    paymentMethod: 'Τρόπος Πληρωμής',
    creditCard: 'Πιστωτική Κάρτα',
    completeOrder: 'Ολοκλήρωση Παραγγελίας',
    processing: 'Επεξεργασία...',
    loginRequired: 'Παρακαλώ συνδεθείτε για ολοκλήρωση',
    itemRemoved: 'Το προϊόν αφαιρέθηκε από το καλάθι',
    errorRemoving: 'Σφάλμα κατά την αφαίρεση προϊόντος',
    checkoutFailed: 'Η ολοκλήρωση απέτυχε. Παρακαλώ δοκιμάστε ξανά.',
    freeShipping: 'Δωρεάν Αποστολή',
    freeShippingOn: 'Δωρεάν Αποστολή σε παραγγελίες',
    discountPercent: 'Έκπτωση 10%',
    couponCode: 'Κωδικός Κουπονιού',
    couponPlaceholder: 'Εισάγετε κωδικό κουπονιού',
    applyCoupon: 'Εφαρμογή',
    removeCoupon: 'Αφαίρεση',
    couponApplied: 'Το κουπόνι εφαρμόστηκε!',
    couponInvalid: 'Μη έγκυρος κωδικός κουπονιού',
    couponDiscount: 'Κουπόνι',
    youMayAlsoLike: 'Μπορεί Επίσης να Σας Αρέσει',
    addToCartShort: 'Προσθήκη'
  },
  de: {
    yourCart: 'Ihr Warenkorb',
    emptyCart: 'Ihr Warenkorb ist leer',
    emptyCartDesc: 'Sie haben noch keine Briefmarken in Ihren Warenkorb gelegt.',
    browseCatalog: 'Durchsuchen Sie unseren Katalog',
    quantity: 'Menge',
    remove: 'Entfernen',
    removeItem: 'Artikel Entfernen',
    subtotal: 'Zwischensumme',
    shipping: 'Versand',
    shippingFee: 'Einschreiben',
    discount: 'Rabatt',
    total: 'Gesamt',
    proceedCheckout: 'Zur Kasse',
    backShopping: 'Weiter Einkaufen',
    shippingInfo: 'Versandinformationen',
    fullName: 'Vollständiger Name',
    email: 'E-Mail',
    address: 'Adresse',
    city: 'Stadt',
    country: 'Land',
    zipCode: 'Postleitzahl',
    paymentMethod: 'Zahlungsmethode',
    creditCard: 'Kreditkarte',
    completeOrder: 'Bestellung Abschließen',
    processing: 'Wird bearbeitet...',
    loginRequired: 'Bitte melden Sie sich an, um zur Kasse zu gehen',
    itemRemoved: 'Artikel aus dem Warenkorb entfernt',
    errorRemoving: 'Fehler beim Entfernen des Artikels',
    checkoutFailed: 'Kasse fehlgeschlagen. Bitte versuchen Sie es erneut.',
    freeShipping: 'Kostenloser Versand',
    freeShippingOn: 'Kostenloser Versand bei Bestellungen',
    discountPercent: '10% Rabatt',
    couponCode: 'Gutscheincode',
    couponPlaceholder: 'Gutscheincode eingeben',
    applyCoupon: 'Anwenden',
    removeCoupon: 'Entfernen',
    couponApplied: 'Gutschein angewendet!',
    couponInvalid: 'Ungültiger Gutscheincode',
    couponDiscount: 'Gutschein',
    youMayAlsoLike: 'Das Könnte Ihnen Auch Gefallen',
    addToCartShort: 'Hinzufügen'
  }
};

const CartPage = () => {
  const navigate = useNavigate();
  const { cart, fetchCart, user, language, currencySymbols, currencyRates } = useContext(AppContext);
  const t = translations[language] || translations.en;
  const [showCheckout, setShowCheckout] = useState(true);
  const [showMorePayments, setShowMorePayments] = useState(false);
  const [shippingAddress, setShippingAddress] = useState({
    fullName: '',
    email: '',
    address: '',
    city: '',
    country: '',
    zipCode: ''
  });
  const [currency, setCurrency] = useState('USD');
  const displayTotal = (usdAmount) => {
    const rate = currencyRates?.[currency] || 1;
    return `${currencySymbols?.[currency] || '$'}${(usdAmount * rate).toFixed(2)}`;
  };
  const [paymentMethod, setPaymentMethod] = useState('card');
  const [processing, setProcessing] = useState(false);
  const [draftId, setDraftId] = useState(null);
  const [showInlinePayment, setShowInlinePayment] = useState(false);
  const [inlinePaymentTotal, setInlinePaymentTotal] = useState(0);
  const [couponCode, setCouponCode] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [couponLoading, setCouponLoading] = useState(false);
  const [crossSellProducts, setCrossSellProducts] = useState([]);
  const [totals, setTotals] = useState({
    subtotal: 0,
    discount: 0,
    coupon_discount: 0,
    coupon_code: null,
    shipping: 0,
    total: 0,
    free_shipping_eligible: false,
    discount_eligible: false,
    amount_to_free_shipping: 0
  });

  // Fetch cart totals
  React.useEffect(() => {
    fetchCartTotals();
  }, [cart, appliedCoupon]);

  // Fetch cross-sell products based on cart categories
  React.useEffect(() => {
    const fetchCrossSell = async () => {
      if (!cart || cart.length === 0) return;
      try {
        const cartProductIds = cart.map(i => i.product_id || i.product?.id).filter(Boolean);
        const categories = [...new Set(cart.flatMap(i => i.product?.categories || []))];
        const cat = categories[0] || '';
        const response = await axios.get(`${API}/products?category=${cat}&limit=20&active_only=true`);
        const products = response.data.products || response.data;
        const filtered = (Array.isArray(products) ? products : [])
          .filter(p => !cartProductIds.includes(p.id) && p.stock > 0 && !p.deleted && !p.archived)
          .slice(0, 4);
        setCrossSellProducts(filtered);
      } catch (error) {
        console.debug('Cross-sell fetch skipped');
      }
    };
    fetchCrossSell();
  }, [cart]);

  // Auto-fill from user account and load saved address
  React.useEffect(() => {
    if (user && showCheckout) {
      loadUserAddress();
    }
  }, [user, showCheckout]);

  const fetchCartTotals = async () => {
    try {
      const params = appliedCoupon ? `?coupon_code=${appliedCoupon}` : '';
      const response = await axios.get(`${API}/cart/totals${params}`, { withCredentials: true });
      setTotals(response.data);
    } catch (error) {
      console.error('Error fetching cart totals:', error);
    }
  };

  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) return;
    setCouponLoading(true);
    try {
      await axios.post(`${API}/coupons/validate?code=${couponCode.trim()}&subtotal=${totals.subtotal}`);
      setAppliedCoupon(couponCode.trim().toUpperCase());
      toast.success(t.couponApplied);
    } catch (error) {
      toast.error(error.response?.data?.detail || t.couponInvalid);
    } finally {
      setCouponLoading(false);
    }
  };

  const handleRemoveCoupon = () => {
    setAppliedCoupon(null);
    setCouponCode('');
  };

  const loadUserAddress = async () => {
    try {
      // Auto-fill name and email
      setShippingAddress(prev => ({
        ...prev,
        fullName: user.name || '',
        email: user.email || ''
      }));

      // Load saved address if exists
      const response = await axios.get(`${API}/user/address`, { withCredentials: true });
      if (response.data) {
        setShippingAddress({
          fullName: response.data.full_name || user.name || '',
          email: response.data.email || user.email || '',
          address: response.data.address || '',
          city: response.data.city || '',
          country: response.data.country || '',
          zipCode: response.data.zip_code || ''
        });
      }
    } catch (error) {
      console.error('Error loading address:', error);
      // Just use name/email auto-fill if address load fails
    }
  };

  const handleRemove = async (itemId) => {
    try {
      const removedItem = cart.find(item => item.id === itemId);
      await axios.delete(`${API}/cart/${itemId}`, { withCredentials: true });
      if (removedItem?.product) trackRemoveFromCart(removedItem.product, removedItem.quantity);
      fetchCart();
      toast.success(t.itemRemoved);
    } catch (error) {
      console.error('Error removing item:', error);
      toast.error(t.errorRemoving);
    }
  };

  const handleUpdateQuantity = async (productId, newQuantity) => {
    if (newQuantity < 1) return;
    try {
      await axios.post(`${API}/cart/add?product_id=${productId}&quantity=${newQuantity}`, {}, { withCredentials: true });
      fetchCart();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update quantity');
    }
  };

  const calculateTotal = () => {
    return cart.reduce((total, item) => total + (item.product?.price || 0) * item.quantity, 0).toFixed(2);
  };

  const handleCheckout = async () => {
    if (!shippingAddress.fullName || !shippingAddress.email || !shippingAddress.address) {
      toast.error('Please fill in all shipping details');
      return;
    }

    setProcessing(true);
    trackBeginCheckout(cart.map(i => i.product).filter(Boolean), parseFloat(calculateTotal()), currency);

    let savedDraftId = null;
    try {
      // STEP 1: Save order draft FIRST (before payment attempt)
      const draftResponse = await axios.post(
        `${API}/checkout/save-draft?` + new URLSearchParams({
          full_name: shippingAddress.fullName,
          email: shippingAddress.email,
          address: shippingAddress.address,
          city: shippingAddress.city,
          country: shippingAddress.country,
          zip_code: shippingAddress.zipCode,
          currency: currency,
          payment_method: paymentMethod,
          ...(appliedCoupon ? { coupon_code: appliedCoupon } : {})
        }),
        {},
        { 
          withCredentials: true,
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );
      
      savedDraftId = draftResponse.data.draft_id;
      setDraftId(savedDraftId);
      toast.success('Order information saved!');
      
      // STEP 2: Now proceed with payment based on selected method
      if (paymentMethod === 'card') {
        if (isStripeInlineAvailable()) {
          setInlinePaymentTotal(draftResponse.data.total || parseFloat(calculateTotal()));
          setShowInlinePayment(true);
          setProcessing(false);
          return;
        }
        const response = await axios.post(
          `${API}/checkout/create-session?draft_id=${savedDraftId}&currency=${currency.toLowerCase()}`,
          shippingAddress,
          {
            withCredentials: true,
            headers: {
              'Content-Type': 'application/json',
              'Origin': window.location.origin
            }
          }
        );
        window.location.href = response.data.url;

      } else if (['crypto', 'wise', 'revolut', 'paypal', 'eft'].includes(paymentMethod)) {
        // Manual payment methods: create the order, then show the instructions
        // page. order_id goes in the URL so the page survives a refresh.
        const orderReference = `ORD-${new Date().toISOString().split('T')[0].replace(/-/g, '')}-${savedDraftId.substring(0, 8).toUpperCase()}`;

        const orderRes = await axios.post(
          `${API}/checkout/create-order-from-draft`,
          null,
          {
            params: {
              draft_id: savedDraftId,
              payment_method: paymentMethod,
              payment_reference: orderReference
            },
            withCredentials: true
          }
        );

        // Refresh cart (will be empty after order creation)
        await fetchCart();

        const orderTotal = orderRes.data.total ?? draftResponse.data.total ?? 0;
        navigate(`/checkout/${paymentMethod}?order_id=${orderRes.data.order_id}`, {
          state: {
            orderReference: orderReference,
            totalAmount: Number(orderTotal).toFixed(2),
            currency: orderRes.data.currency || currency,
            draftId: savedDraftId
          }
        });
      }
      
    } catch (error) {
      console.error('Checkout error:', error);

      // Even if payment setup fails, the draft is already saved!
      // (checked via the local variable — the draftId state update isn't
      // visible inside this closure yet)
      if (savedDraftId) {
        toast.error('Payment setup failed, but your order information has been saved. Please try again.');
      } else {
        toast.error(error.response?.data?.detail || 'Checkout failed. Please try again.');
      }

      setProcessing(false);
    }
  };

  if (cart.length === 0) {
    return (
      <div>
        <Header />
        <div className="max-w-7xl mx-auto px-4 py-16 text-center">
          <ShoppingBag size={80} className="mx-auto mb-6 text-gray-300" />
          <h2 className="text-3xl font-bold mb-4" data-testid="empty-cart-title">{t.emptyCart}</h2>
          <p className="text-gray-600 mb-8">{t.emptyCartDesc}</p>
          <button onClick={() => navigate('/shop')} className="btn-primary" data-testid="continue-shopping-btn">
            {t.browseCatalog}
          </button>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="cart-page">
      <SEO
        title="Shopping Cart | North Cyprus Philately"
        description="Review your stamp collection cart and proceed to secure checkout. Multiple payment methods available with worldwide shipping."
        url="https://www.northcyprusphilately.com/cart"
        noindex={true}
      />
      <Header />
      
      <div className="max-w-7xl mx-auto px-4 py-8">
        <h1 className="text-4xl font-bold mb-8" data-testid="cart-title">{t.yourCart}</h1>
        
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            {cart.map((item) => (
              <div key={item.id} className="cart-item flex gap-4" data-testid={`cart-item-${item.id}`}>
                <img
                  src={item.product?.images?.[0] || 'https://via.placeholder.com/120'}
                  alt={`${item.product?.title} - ${item.product?.condition || 'collectible'} stamp`}
                  loading="lazy"
                  width="96"
                  height="96"
                  className="w-24 h-24 object-cover rounded"
                />
                <div className="flex-1">
                  <h3 className="text-lg font-semibold mb-1">{item.product?.title}</h3>
                  <p className="text-sm text-gray-600 mb-2">{item.product?.condition}</p>
                  <div className="flex items-center gap-4">
                    <span className="text-xl font-bold" style={{color: '#B8941E'}}>${item.product?.price}</span>
                    <div className="flex items-center gap-2 border rounded-lg">
                      <button
                        onClick={() => handleUpdateQuantity(item.product_id, item.quantity - 1)}
                        disabled={item.quantity <= 1}
                        className="p-1.5 hover:bg-gray-100 rounded-l-lg disabled:opacity-30 transition"
                      >
                        <Minus size={14} />
                      </button>
                      <span className="text-sm font-semibold w-8 text-center">{item.quantity}</span>
                      <button
                        onClick={() => handleUpdateQuantity(item.product_id, item.quantity + 1)}
                        className="p-1.5 hover:bg-gray-100 rounded-r-lg transition"
                      >
                        <Plus size={14} />
                      </button>
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => handleRemove(item.id)}
                  className="text-red-500 hover:text-red-700 transition"
                  data-testid={`remove-item-${item.id}`}
                  aria-label={`Remove ${item.product?.title} from cart`}
                >
                  <Trash2 size={20} aria-hidden="true" />
                </button>
              </div>
            ))}
          </div>
          
          <div>
            <div className="bg-white p-6 rounded-lg shadow-lg">
              <h2 className="text-2xl font-bold mb-4" data-testid="order-summary-title">{t.total}</h2>
              
              {/* Shipping & Discount Info Banner - Localized */}
              <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm">
                <div className="flex items-center gap-2 text-blue-800 font-semibold mb-1">
                  🚢 {t.shippingFee}: $4
                </div>
                <div className="flex items-center gap-2 text-green-800 font-semibold mb-1">
                  ✓ {t.freeShippingOn} $100+
                </div>
                <div className="flex items-center gap-2 text-green-800 font-semibold">
                  ✓ {t.discountPercent} $100+
                </div>
                {!totals.free_shipping_eligible && totals.amount_to_free_shipping > 0 && (
                  <div className="mt-2 text-xs text-gray-700">
                    {language === 'en' && `Add $${totals.amount_to_free_shipping.toFixed(2)} more for free shipping & discount!`}
                    {language === 'tr' && `Ücretsiz kargo ve indirim için $${totals.amount_to_free_shipping.toFixed(2)} daha ekleyin!`}
                    {language === 'el' && `Προσθέστε $${totals.amount_to_free_shipping.toFixed(2)} ακόμα για δωρεάν αποστολή και έκπτωση!`}
                    {language === 'de' && `Fügen Sie $${totals.amount_to_free_shipping.toFixed(2)} hinzu für kostenlosen Versand und Rabatt!`}
                  </div>
                )}
              </div>

              {/* Coupon Code Input */}
              <div className="mb-4">
                <label className="text-sm font-semibold mb-2 block">{t.couponCode}</label>
                {appliedCoupon ? (
                  <div className="flex items-center justify-between bg-green-50 border border-green-200 rounded-lg px-3 py-2">
                    <span className="text-green-700 font-semibold text-sm">{appliedCoupon}</span>
                    <button onClick={handleRemoveCoupon} className="text-red-500 text-sm hover:underline">
                      {t.removeCoupon}
                    </button>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={couponCode}
                      onChange={(e) => setCouponCode(e.target.value)}
                      placeholder={t.couponPlaceholder}
                      className="flex-1 px-3 py-2 border rounded text-sm focus:ring-2 focus:ring-[#C8A951] focus:border-transparent"
                      onKeyDown={(e) => e.key === 'Enter' && handleApplyCoupon()}
                    />
                    <button
                      onClick={handleApplyCoupon}
                      disabled={couponLoading || !couponCode.trim()}
                      className="btn-secondary text-sm px-4 disabled:opacity-50"
                    >
                      {t.applyCoupon}
                    </button>
                  </div>
                )}
              </div>

              <div className="space-y-2 mb-4">
                <div className="flex justify-between">
                  <span>{t.subtotal}</span>
                  <span>${totals.subtotal.toFixed(2)}</span>
                </div>
                {totals.discount > 0 && (
                  <div className="flex justify-between text-green-600">
                    <span>{t.discount} (10%)</span>
                    <span>-${totals.discount.toFixed(2)}</span>
                  </div>
                )}
                {totals.coupon_discount > 0 && (
                  <div className="flex justify-between text-green-600">
                    <span>{t.couponDiscount} ({totals.coupon_code})</span>
                    <span>-${totals.coupon_discount.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span>{t.shipping}</span>
                  <span className={totals.shipping === 0 ? 'text-green-600 font-semibold' : ''}>
                    {totals.shipping === 0 ? t.freeShipping : `$${totals.shipping.toFixed(2)}`}
                  </span>
                </div>
              </div>
              <div className="border-t pt-4 mb-6">
                <div className="flex justify-between text-xl font-bold">
                  <span>{t.total}</span>
                  <span style={{color: '#B8941E'}} data-testid="cart-total">{displayTotal(totals.total)}</span>
                </div>
              </div>
              
              {/* Guest Checkout Banner */}
              <div className="mb-4 p-3 bg-green-50 border border-green-300 rounded-lg">
                <p className="text-sm font-semibold text-green-800 flex items-center gap-2">
                  ✅ {language === 'tr' ? 'Hesap gerektirmez — adresinizi girin ve ödeyin!' :
                      language === 'de' ? 'Kein Konto nötig — Adresse eingeben und bezahlen!' :
                      language === 'el' ? 'Χωρίς λογαριασμό — συμπληρώστε τη διεύθυνση και πληρώστε!' :
                      'No account needed — just fill in your address and pay!'}
                </p>
              </div>

              {/* Checkout Form — always visible */}
              <div className="space-y-3">
                <h3 className="font-semibold text-base">{t.shippingInfo}</h3>

                <input
                  type="text"
                  placeholder={`${t.fullName} *`}
                  value={shippingAddress.fullName}
                  onChange={(e) => setShippingAddress({...shippingAddress, fullName: e.target.value})}
                  className="w-full"
                  data-testid="shipping-fullname"
                />
                <input
                  type="email"
                  placeholder={`${t.email} *`}
                  value={shippingAddress.email}
                  onChange={(e) => setShippingAddress({...shippingAddress, email: e.target.value})}
                  className="w-full"
                  data-testid="shipping-email"
                />
                <input
                  type="text"
                  placeholder={`${t.address} *`}
                  value={shippingAddress.address}
                  onChange={(e) => setShippingAddress({...shippingAddress, address: e.target.value})}
                  className="w-full"
                  data-testid="shipping-address"
                />
                <div className="grid grid-cols-2 gap-2">
                  <input
                    type="text"
                    placeholder={`${t.city} *`}
                    value={shippingAddress.city}
                    onChange={(e) => setShippingAddress({...shippingAddress, city: e.target.value})}
                    className="w-full"
                    data-testid="shipping-city"
                  />
                  <input
                    type="text"
                    placeholder={t.zipCode}
                    value={shippingAddress.zipCode}
                    onChange={(e) => setShippingAddress({...shippingAddress, zipCode: e.target.value})}
                    className="w-full"
                    data-testid="shipping-zipcode"
                  />
                </div>
                <select
                  value={shippingAddress.country}
                  onChange={(e) => setShippingAddress({...shippingAddress, country: e.target.value})}
                  className="w-full"
                  data-testid="shipping-country"
                >
                  <option value="">{t.country} *</option>
                  {["Afghanistan","Albania","Algeria","Argentina","Armenia","Australia","Austria","Azerbaijan","Bahrain","Bangladesh","Belarus","Belgium","Bolivia","Bosnia and Herzegovina","Brazil","Bulgaria","Cambodia","Cameroon","Canada","Chile","China","Colombia","Costa Rica","Croatia","Cuba","Cyprus","Czech Republic","Denmark","Dominican Republic","Ecuador","Egypt","Estonia","Ethiopia","Finland","France","Georgia","Germany","Ghana","Greece","Guatemala","Honduras","Hong Kong","Hungary","Iceland","India","Indonesia","Iran","Iraq","Ireland","Israel","Italy","Jamaica","Japan","Jordan","Kazakhstan","Kenya","Kuwait","Latvia","Lebanon","Libya","Lithuania","Luxembourg","Malaysia","Malta","Mexico","Moldova","Mongolia","Morocco","Myanmar","Nepal","Netherlands","New Zealand","Nigeria","North Cyprus","North Macedonia","Norway","Oman","Pakistan","Panama","Paraguay","Peru","Philippines","Poland","Portugal","Qatar","Romania","Russia","Saudi Arabia","Serbia","Singapore","Slovakia","Slovenia","South Africa","South Korea","Spain","Sri Lanka","Sudan","Sweden","Switzerland","Syria","Taiwan","Tanzania","Thailand","Trinidad and Tobago","Tunisia","Turkey","Ukraine","United Arab Emirates","United Kingdom","United States","Uruguay","Uzbekistan","Venezuela","Vietnam"].map(c => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>

                {/* Payment: Credit Card First */}
                <div className="pt-2">
                  <label className="block font-semibold mb-2 text-sm">{t.paymentMethod}</label>

                  {/* Primary: Card */}
                  <label className={`flex items-center gap-3 p-3 border-2 rounded-lg cursor-pointer mb-2 transition ${paymentMethod === 'card' ? 'border-[#C8A951] bg-amber-50' : 'border-gray-200 hover:border-gray-300'}`}>
                    <input type="radio" name="payment" value="card" checked={paymentMethod === 'card'} onChange={() => setPaymentMethod('card')} className="accent-[#C8A951]" data-testid="payment-method" />
                    <span className="text-2xl">💳</span>
                    <div>
                      <p className="font-semibold text-sm">{language === 'tr' ? 'Kredi / Banka Kartı' : 'Credit / Debit Card'}</p>
                      <p className="text-xs text-gray-500">Visa, Mastercard, Amex — Powered by Stripe</p>
                    </div>
                  </label>

                  {/* Secondary: PayPal */}
                  <label className={`flex items-center gap-3 p-3 border-2 rounded-lg cursor-pointer mb-2 transition ${paymentMethod === 'paypal' ? 'border-[#C8A951] bg-amber-50' : 'border-gray-200 hover:border-gray-300'}`}>
                    <input type="radio" name="payment" value="paypal" checked={paymentMethod === 'paypal'} onChange={() => setPaymentMethod('paypal')} className="accent-[#C8A951]" />
                    <span className="text-2xl">🅿️</span>
                    <div>
                      <p className="font-semibold text-sm">PayPal</p>
                      <p className="text-xs text-gray-500">{language === 'tr' ? 'PayPal hesabınızla güvenli ödeme' : 'Pay securely with your PayPal account'}</p>
                    </div>
                  </label>

                  {/* More payment options toggle */}
                  <button type="button" onClick={() => setShowMorePayments(v => !v)} className="text-xs text-gray-500 hover:text-[#B8941E] transition flex items-center gap-1 mb-2">
                    {showMorePayments ? '▲' : '▼'} {language === 'tr' ? 'Diğer ödeme seçenekleri (Crypto, Wise, Revolut, Havale)' : 'More payment options (Crypto, Wise, Revolut, Bank Transfer)'}
                  </button>

                  {showMorePayments && (
                    <div className="space-y-2">
                      {[
                        { value: 'crypto', icon: '₿', label: 'Cryptocurrency', sub: 'Bitcoin via BitPay' },
                        { value: 'wise', icon: '🌍', label: 'Wise', sub: '@ahmetk3140' },
                        { value: 'revolut', icon: '🔄', label: 'Revolut', sub: '@ahmettjhv' },
                        { value: 'eft', icon: '🏦', label: language === 'tr' ? 'Banka Havalesi (IBAN)' : 'Bank Transfer (EFT/IBAN)', sub: '' }
                      ].map(opt => (
                        <label key={opt.value} className={`flex items-center gap-3 p-3 border-2 rounded-lg cursor-pointer transition ${paymentMethod === opt.value ? 'border-[#C8A951] bg-amber-50' : 'border-gray-200 hover:border-gray-300'}`}>
                          <input type="radio" name="payment" value={opt.value} checked={paymentMethod === opt.value} onChange={() => setPaymentMethod(opt.value)} className="accent-[#C8A951]" />
                          <span className="text-xl">{opt.icon}</span>
                          <div>
                            <p className="font-semibold text-sm">{opt.label}</p>
                            {opt.sub && <p className="text-xs text-gray-500">{opt.sub}</p>}
                          </div>
                        </label>
                      ))}
                    </div>
                  )}
                </div>

                {paymentMethod === 'card' && (
                  <div className="flex gap-2">
                    {['USD', 'EUR', 'GBP'].map(c => (
                      <button key={c} type="button" onClick={() => setCurrency(c)}
                        className={`flex-1 py-1.5 rounded border text-sm font-medium transition ${currency === c ? 'bg-[#C8A951] text-white border-[#C8A951]' : 'border-gray-300 hover:border-[#C8A951]'}`}>
                        {c === 'USD' ? '$ USD' : c === 'EUR' ? '€ EUR' : '£ GBP'}
                      </button>
                    ))}
                  </div>
                )}

                {/* Trust badges */}
                <div className="flex items-center justify-center gap-3 py-1 text-xs text-gray-500">
                  <span>🔒 SSL</span>
                  <span>🛡️ Buyer Protection</span>
                  <span>✈️ Worldwide Shipping</span>
                </div>

                {showInlinePayment ? (
                  <div className="p-4 border-2 border-[#C8A951] rounded-xl bg-white" data-testid="inline-payment-container">
                    <StripeInlineCheckout
                      draftId={draftId}
                      total={inlinePaymentTotal}
                      currency={currency}
                      language={language}
                      onSuccess={(orderId) => {
                        if (fetchCart) fetchCart();
                        toast.success(language === 'tr' ? 'Ödeme başarılı!' : 'Payment successful!');
                        navigate(`/checkout/success?order_id=${orderId}`);
                      }}
                      onCancel={() => setShowInlinePayment(false)}
                    />
                  </div>
                ) : (
                <button
                  onClick={handleCheckout}
                  disabled={processing || !shippingAddress.fullName || !shippingAddress.email || !shippingAddress.address || !shippingAddress.city || !shippingAddress.country}
                  className="btn-primary w-full text-lg py-4 disabled:opacity-40 disabled:cursor-not-allowed"
                  data-testid="checkout-btn"
                >
                  {processing ? (
                    <span className="flex items-center justify-center gap-2">
                      <span className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full"></span>
                      {t.processing}
                    </span>
                  ) : (
                    <span>
                      {paymentMethod === 'card'
                        ? `🔒 ${language === 'tr' ? 'Güvenli Öde' : 'Pay Securely'} — ${displayTotal(totals.total)}`
                        : `${t.completeOrder} — ${displayTotal(totals.total)}`}
                    </span>
                  )}
                </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Cross-sell: You May Also Like */}
      {crossSellProducts.length > 0 && (
        <div className="max-w-7xl mx-auto px-4 py-8">
          <h2 className="text-2xl font-bold mb-6">{t.youMayAlsoLike}</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {crossSellProducts.map(product => {
              const title = product[`title_${language}`] || product.title;
              const image = product.images && product.images.length > 0
                ? (product.images[0].startsWith('http') ? product.images[0] : `${BACKEND_URL}${product.images[0]}`)
                : '';
              return (
                <div key={product.id} className="product-card">
                  {image && (
                    <Link to={productUrl(product)} className="relative block">
                      <img src={image} alt={`${title} - TRNC stamp`} loading="lazy" width="300" height="144" className="w-full h-36 object-cover rounded-t-lg"
                        onError={(e) => { e.target.onerror = null; e.target.src = 'https://via.placeholder.com/300x200?text=No+Image'; }} />
                      {product.discount_rate > 0 && (
                        <span className="absolute top-2 left-2 bg-red-500 text-white text-xs font-bold px-2 py-1 rounded">-{product.discount_rate}%</span>
                      )}
                    </Link>
                  )}
                  <div className="p-3">
                    <h3 className="font-semibold text-sm mb-1 line-clamp-2">
                      <Link to={productUrl(product)} className="hover:text-[#C8A951]">{title}</Link>
                    </h3>
                    <div className="flex items-center justify-between">
                      <span className="text-[#B8941E] font-bold text-sm">
                        ${product.discounted_price && product.discounted_price < product.price ? product.discounted_price.toFixed(2) : product.price?.toFixed(2)}
                      </span>
                      <button
                        className="btn-secondary text-xs py-1 px-2 flex items-center gap-1"
                        onClick={async () => {
                          try {
                            await axios.post(`${API}/cart/add?product_id=${product.id}&quantity=1`, {}, { withCredentials: true });
                            fetchCart();
                            toast.success(t.addToCartShort || 'Added to cart');
                          } catch (err) { toast.error('Failed'); }
                        }}
                      >
                        <ShoppingCart size={12} /> {t.addToCartShort}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default CartPage;